// Account Risk section — FE-034 / spec §7.0.9 + Dashboard-Filters-01.
//
// Widget-level filter: risk-level chips (low / medium / high / critical).
// Applied client-side over the returned ``risk_donut`` because BE-029
// always returns the full distribution.

import { useMemo, useState } from "react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useDashboardDateRange,
  useRiskWorkloadQuery,
  type RiskWorkloadResponse,
} from "@/features/dashboard";

import { RiskDonut } from "./RiskDonut";


export const RISK_WORKLOAD_SECTION = "risk-workload" as const;


export type RiskLevel = "low" | "medium" | "high" | "critical";

export const RISK_LEVEL_OPTIONS: Array<{
  value: RiskLevel; label: string; chip: string;
}> = [
  { value: "low", label: "Low", chip: "bg-sage-tint text-teal" },
  { value: "medium", label: "Medium", chip: "bg-teal-tint text-teal" },
  { value: "high", label: "High", chip: "bg-gold-tint text-gold-ink" },
  { value: "critical", label: "Critical", chip: "bg-ember-tint text-ember-ink" },
];


export function RiskWorkloadSection() {
  const filters = useCommonFilters();
  const dateRange = useDashboardDateRange();
  const [activeLevels, setActiveLevels] = useState<RiskLevel[]>([]);
  const query = useRiskWorkloadQuery(filters);

  const filteredData = useMemo<RiskWorkloadResponse | undefined>(() => {
    if (!query.data) return undefined;
    if (activeLevels.length === 0) return query.data;
    const active = new Set(activeLevels);
    const donut = {
      low: active.has("low") ? query.data.risk_donut.low : 0,
      medium: active.has("medium") ? query.data.risk_donut.medium : 0,
      high: active.has("high") ? query.data.risk_donut.high : 0,
      critical: active.has("critical") ? query.data.risk_donut.critical : 0,
    };
    return { ...query.data, risk_donut: donut };
  }, [query.data, activeLevels]);

  function toggleLevel(l: RiskLevel): void {
    setActiveLevels((prev) =>
      prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l],
    );
  }

  return (
    <DashboardCard
      section={RISK_WORKLOAD_SECTION}
      title="Account risk"
      subtitle="Distribution of accounts by risk level."
      actions={
        <RiskLevelChips
          value={activeLevels}
          onToggle={toggleLevel}
        />
      }
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Account risk"
          retry={() => query.refetch()}
        />
      ) : filteredData && !isAllZero(filteredData) ? (
        <RiskDonut
          data={filteredData.risk_donut}
          dateRange={dateRange}
        />
      ) : (
        <DashboardEmptyState
          title="No risky Accounts in this period"
          description="Once Commitments are assigned and any overdue or blocked work appears, the donut fills in here."
        />
      )}
    </DashboardCard>
  );
}


function isAllZero(data: RiskWorkloadResponse): boolean {
  const r = data.risk_donut;
  return r.low === 0 && r.medium === 0 && r.high === 0 && r.critical === 0;
}


export function RiskLevelChips({
  value, onToggle, testId = "risk-level-chips",
}: {
  value: RiskLevel[];
  onToggle: (l: RiskLevel) => void;
  testId?: string;
}) {
  const set = new Set(value);
  return (
    <div
      data-testid={testId}
      data-count={value.length}
      role="group"
      aria-label="Risk level filter"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      <span className="px-1 text-text-tertiary">Risk:</span>
      {RISK_LEVEL_OPTIONS.map((opt) => {
        const active = set.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            data-testid={`${testId}-${opt.value}`}
            data-active={active ? "true" : "false"}
            onClick={() => onToggle(opt.value)}
            className={[
              "rounded px-1.5 py-0.5 text-[11px] font-medium transition-fast ease-out",
              active
                ? opt.chip
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
